/**
 * Response shaping for the ICD-10 endpoints. No transport, so it stays testable on its own.
 *
 *   GET /icd/interpret?q=<text>    search; flat list ranked by `confidence`
 *   GET /icd/hierarchy?code=<code> one level of children; requires auth, case-sensitive
 *
 * Quirks handled below: `confidence` is an unbounded score (1.8 observed) so it only ever orders,
 * some rows carry no description (`name` is the code repeated), and `billable`/`expandable` never
 * both hold. Relevance filtering is the API's job, not this layer's.
 */

/** Read strictly: a missing flag becomes a non-billable category, which cannot be submitted. */
const isBillable = (item) => item?.billable === true;

/**
 * The backend defines expandable as (not billable AND has children), so it is trusted when
 * present. A non-billable code can therefore be a dead end, which must not offer a chevron.
 * The fallback only covers the field being absent.
 */
const isExpandable = (item) =>
  typeof item?.expandable === 'boolean' ? item.expandable : !isBillable(item);

const mapItem = (item) => ({
  code: String(item.code).trim().toUpperCase(),
  description: item.name,
  billable: isBillable(item),
  expandable: isExpandable(item),
  // Drives the result ordering only; never shown.
  confidence: Number(item.confidence) || 0,
});

const dedupeByCode = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item.code).trim().toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * A node the API has no description for, e.g. "T67.2X". These are real rungs of the ICD ladder
 * (T67.2 reaches its billable codes only via T67.2X -> T67.2XX -> T67.2XXA) but carry no
 * information, so search drops them and expansion steps through them.
 */
export const isUndescribedIcdNode = (item) => {
  const name = String(item?.name ?? item?.description ?? '').trim().toUpperCase();
  const code = String(item?.code || '').trim().toUpperCase();
  return !name || name === code;
};

/** Search response -> ranked candidates. Every result the API returns is shown. */
export const normaliseIcdInterpretation = (data = {}) => {
  const received = Array.isArray(data?.items) ? data.items : [];
  // Sorted before de-duping so a repeated code keeps its highest-scoring copy, whatever order the
  // API listed them in. `filter` already gives a fresh array, so the sort cannot touch the input.
  const items = dedupeByCode(
    received
      .filter((item) => item?.code && !isUndescribedIcdNode(item))
      .sort((a, b) => (Number(b.confidence) || 0) - (Number(a.confidence) || 0)),
  ).map(mapItem);

  return {
    query: data?.query,
    items,
    ambiguous: !!data?.ambiguous,
    // The backend flags a garbage query as unmatched even when it still returns weak
    // candidates, so the UI can say "nothing found" instead of showing them as options.
    unmatched: data?.resolutionMethod === 'unmatched',
  };
};

/** One level of a hierarchy response. Order is the API's, which is ascending by code. */
export const normaliseHierarchyChildren = (data) => {
  const children = Array.isArray(data?.children) ? data.children : [];
  return dedupeByCode(children.filter((child) => child?.code)).map(mapItem);
};
